import { formatDateTime } from "@/shared/lib/date-time";
import {
  Button,
  FormRoot,
  FormTextarea,
  Label,
  Modal,
  RHF,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";

const CorrectInstructorCheckInSchema = z.object({
  correctInstructorId: z.string().min(1, "ຕ້ອງເລືອກຄູສອນທີ່ຖືກຕ້ອງ"),
  reason: z.string().min(1, "ຕ້ອງໃສ່ເຫດຜົນ"),
});

type CorrectInstructorCheckInFormValues = z.infer<
  typeof CorrectInstructorCheckInSchema
>;

export type InstructorCheckInAttendee = {
  id: string;
  instructorId: string;
  instructorName: string;
  checkedInAt: string;
  isSubstitute: boolean | null;
  instructorRole?: "lead" | "assist" | null;
};

type CorrectInstructorCheckInDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wrongAttendee: InstructorCheckInAttendee | null;
  correctOptions: Array<{ id: string; name: string }>;
  onConfirm: (values: {
    wrongCheckInId: string;
    correctInstructorId: string;
    reason: string;
  }) => void;
  isSubmitting?: boolean;
};

export function CorrectInstructorCheckInDialog({
  open,
  onOpenChange,
  wrongAttendee,
  correctOptions,
  onConfirm,
  isSubmitting = false,
}: CorrectInstructorCheckInDialogProps) {
  const methods = RHF.useForm<CorrectInstructorCheckInFormValues>({
    resolver: zodResolver(CorrectInstructorCheckInSchema),
    defaultValues: {
      correctInstructorId: "",
      reason: "",
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        correctInstructorId: correctOptions[0]?.id ?? "",
        reason: "",
      });
    }
  }, [open, correctOptions, methods]);

  const handleSubmit = (values: CorrectInstructorCheckInFormValues) => {
    if (!wrongAttendee) return;
    onConfirm({
      wrongCheckInId: wrongAttendee.id,
      correctInstructorId: values.correctInstructorId,
      reason: values.reason,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) methods.reset();
    onOpenChange(next);
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="ແກ້ໄຂການກວດຊື່ຄູສອນ"
      size="md"
    >
      <FormRoot
        methods={methods}
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {wrongAttendee && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <p className="font-medium text-destructive">ກວດຊື່ຜິດ</p>
            <p className="mt-1">{wrongAttendee.instructorName}</p>
            <p className="text-muted-foreground text-xs">
              {formatDateTime(wrongAttendee.checkedInAt)}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>ຄູສອນທີ່ຖືກຕ້ອງ</Label>
          <Select
            value={methods.watch("correctInstructorId")}
            onValueChange={(value) =>
              methods.setValue("correctInstructorId", value, {
                shouldValidate: true,
              })
            }
            disabled={isSubmitting || correctOptions.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="ເລືອກຄູສອນ" />
            </SelectTrigger>
            <SelectContent>
              {correctOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {methods.formState.errors.correctInstructorId && (
            <p className="text-destructive text-xs">
              {methods.formState.errors.correctInstructorId.message}
            </p>
          )}
          {correctOptions.length === 0 && (
            <p className="text-muted-foreground text-xs">
              ບໍ່ມີຄູທີ່ assign ແລ້ວທີ່ຍັງບໍ່ໄດ້ກວດຊື່
            </p>
          )}
        </div>

        <FormTextarea
          name="reason"
          label="ເຫດຜົນ"
          placeholder="ປ້ອນເຫດຜົນການແກ້ໄຂ (ບັງຄັບ)"
          rows={3}
          requiredMark
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || correctOptions.length === 0}
          >
            {isSubmitting ? "ກຳລັງບັນທຶກ..." : "ຢືນຢັນແກ້ໄຂ"}
          </Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
